import React, { useEffect, useState, useCallback } from "react";
import useAxios from "../../../utility/axios-token-manager/init";

const PRIMARY = "#a7cc48";

function Stars({ rating }) {
  if (!rating) return <span style={{ color: "#9ca3af", fontSize: 13 }}>No rating</span>;
  return (
    <span>
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} style={{ color: s <= rating ? "#fbbf24" : "#d1d5db", fontSize: 18 }}>★</span>
      ))}
    </span>
  );
}

export default function AppFeedbacks() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [page, setPage]           = useState(1);
  const LIMIT = 20;

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await useAxios.get(`/admin/app-feedbacks?page=${page}&limit=${LIMIT}`);
      if (res.data.success) {
        setFeedbacks(res.data.feedbacks);
        setTotal(res.data.total);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load feedbacks");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchFeedbacks(); }, [fetchFeedbacks]);

  const totalPages = Math.ceil(total / LIMIT);
  const rated      = feedbacks.filter((f) => f.rating);
  const avgRating  = rated.length
    ? (rated.reduce((sum, f) => sum + f.rating, 0) / rated.length).toFixed(1)
    : null;

  return (
    <div style={S.wrap}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>App Feedbacks</h2>
          <p style={S.sub}>
            {total} feedback{total !== 1 ? "s" : ""}
            {avgRating && ` · Average rating: ${avgRating} / 5`}
          </p>
        </div>
        <button onClick={fetchFeedbacks} style={S.refreshBtn}>↻ Refresh</button>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {loading ? (
        <div style={S.center}><div style={S.spinner} /></div>
      ) : feedbacks.length === 0 ? (
        <div style={S.center}><p style={{ color: "#9ca3af" }}>No feedbacks yet.</p></div>
      ) : (
        <div style={S.grid}>
          {feedbacks.map((fb) => (
            <div key={fb._id} style={S.card}>
              <div style={S.cardTop}>
                <div>
                  <span style={S.merchantName}>{fb.merchantName || "Unknown Merchant"}</span>
                  {fb.merchantEmail && (
                    <span style={S.merchantEmail}> · {fb.merchantEmail}</span>
                  )}
                </div>
                <span style={S.date}>
                  {new Date(fb.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Stars rating={fb.rating} />
              </div>
              <p style={S.message}>{fb.message}</p>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div style={S.pagination}>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={S.pageBtn}>← Prev</button>
          <span style={{ color: "#374151", fontSize: 14 }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={S.pageBtn}>Next →</button>
        </div>
      )}
    </div>
  );
}

const S = {
  wrap:         { fontFamily: "sans-serif", padding: "0 4px" },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 },
  title:        { margin: 0, fontSize: 22, fontWeight: 700, color: "#111827" },
  sub:          { margin: "4px 0 0", color: "#6b7280", fontSize: 14 },
  refreshBtn:   { padding: "6px 14px", borderRadius: 20, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 13 },
  errorBox:     { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 16px", color: "#dc2626", marginBottom: 16 },
  center:       { textAlign: "center", padding: "60px 0" },
  spinner:      { width: 36, height: 36, border: "3px solid #e5e7eb", borderTop: `3px solid ${PRIMARY}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" },
  grid:         { display: "grid", gap: 14 },
  card:         { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "16px 18px" },
  cardTop:      { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 4 },
  merchantName: { fontWeight: 600, color: "#111827", fontSize: 14 },
  merchantEmail:{ color: "#6b7280", fontSize: 12 },
  date:         { color: "#9ca3af", fontSize: 12, whiteSpace: "nowrap" },
  message:      { margin: 0, color: "#374151", fontSize: 14, lineHeight: 1.6 },
  pagination:   { display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 20 },
  pageBtn:      { padding: "6px 14px", border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer", background: "#fff", fontSize: 14 },
};
